import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  AsignaturaFields,
  iDataAsignatura,
} from "../../../interfaces/SuperAdmin/Pages/iDataAsignatura";
import {
  deleteAsignaturas,
  getAsignaturasByOrgId,
  postAsignatura,
  putAsignaturaById,
} from "../../../api/broker/asignatura";
import { toast } from "react-toastify";

interface AsignaturasContextProps {
  asignaturas: iDataAsignatura[];

  toEditAsignatura: iDataAsignatura | null;
  selectedAsignaturas: string[];
  setSelectedAsignaturas: Dispatch<SetStateAction<string[]>>;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;

  isEditModalOpen: boolean;
  isCreateModalOpen: boolean;
  isDeleteModalOpen: boolean;

  isLoading: boolean;
  isEditing: boolean;
  isCreating: boolean;
  isDeleting: boolean;

  handleEditAsignatura: (asignatura: iDataAsignatura) => void;
  toggleEditModal: (isOpen: boolean) => void;
  toggleCreateModal: (isOpen: boolean) => void;
  toggleDeleteModal: (isOpen: boolean) => void;

  asignaturaEdit: (
    data: AsignaturaFields,
    id_asignatura: string
  ) => Promise<void>;
  asignaturaCreate: (
    data: AsignaturaFields,
    id_organizacion: string
  ) => Promise<void>;
  asignaturaDelete: (body: { ids: string[] }) => Promise<void>;
}

const AsignaturasContext = createContext<AsignaturasContextProps>(
  {} as AsignaturasContextProps
);

interface Props {
  children: React.ReactNode;
}

const AsignaturasProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();

  const [asignaturas, setAsignaturas] = useState<iDataAsignatura[]>([]);

  const [toEditAsignatura, setToEditAsignatura] =
    useState<iDataAsignatura | null>(null);
  const [selectedAsignaturas, setSelectedAsignaturas] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Función para extraer asignaturas
  const fetchAsignaturas = async (id: string) => {
    setIsLoading(true);
    try {
      const asignaturasRes = await getAsignaturasByOrgId(id);
      console.log(asignaturasRes);

      if (asignaturasRes.data.statusCode === 200) {
        setAsignaturas(asignaturasRes.data.body);
      } else {
        toast.error("Error al obtener las asignaturas de la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las asignaturas de la organización");
    } finally {
      setIsLoading(false);
    }
  };

  // Crear asignatura
  const asignaturaCreate = async (
    asignatura: AsignaturaFields,
    id_organizacion: string
  ) => {
    setIsCreating(true);
    try {
      //Creamos la asignatura
      const asignaturaRes = await postAsignatura(asignatura, id_organizacion);
      console.log(asignaturaRes);

      if (asignaturaRes.data.statusCode === 201) {
        setAsignaturas((prev) => [...prev, asignaturaRes.data.body]);
        toggleCreateModal(false);
        toast.success("Asignatura creada exitosamente");
      } else {
        toast.error("Error al crear asignatura en la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al crear asignatura en la organización");
    } finally {
      setIsCreating(false);
    }
  };

  // Edicion de asignatura
  const asignaturaEdit = async (
    data: AsignaturaFields,
    id_asignatura: string
  ) => {
    setIsEditing(true);
    try {
      const res = await putAsignaturaById(data, id_asignatura);
      console.log(res);
      if (res.data.statusCode === 200) {
        setAsignaturas((prev) =>
          prev.map((asignatura) => {
            if (asignatura.id_asignatura === id_asignatura) {
              return res.data.body;
            }
            return asignatura;
          })
        );
        toggleEditModal(false);
        toast.success("Asignatura editada exitosamente");
      } else {
        toast.error("Error al editar asignatura");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar asignatura");
    } finally {
      setIsEditing(false);
    }
  };

  // Eliminar asignaturas
  const asignaturaDelete = async (body: { ids: string[] }) => {
    setIsDeleting(true);
    try {
      const res = await deleteAsignaturas(body);
      console.log(res);
      if (res.data.statusCode === 200) {
        setSelectedAsignaturas([]);
        setAsignaturas((prev) =>
          prev.filter((item) => !body.ids.includes(item.id_asignatura))
        );
        toggleDeleteModal(false);
        toast.success("Asignaturas eliminadas exitosamente");
      } else {
        toast.error("Error al eliminar las asignaturas de la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar las asignaturas de la organización");
    } finally {
      setIsDeleting(false);
    }
  };

  // Abrir menu de editar asignatura
  const toggleEditModal = (isOpen: boolean) => {
    if (!isOpen) {
      setToEditAsignatura(null);
    }
    setIsEditModalOpen(isOpen);
  };

  // Abrir menu de crear asignatura
  const toggleCreateModal = (isOpen: boolean) => {
    setIsCreateModalOpen(isOpen);
  };

  // Abrir menu de eliminar asignaturas
  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  // Seleccionar asignatura a editar
  const handleEditAsignatura = (asignatura: iDataAsignatura) => {
    setToEditAsignatura(asignatura);
    toggleEditModal(true);
  };

  useEffect(() => {
    if (idOrganization) {
      fetchAsignaturas(idOrganization);
    }
  }, [idOrganization]);
  return (
    <AsignaturasContext.Provider
      value={{
        asignaturas,
        setSelectedAsignaturas,
        toEditAsignatura,
        selectedAsignaturas,
        searchText,
        setSearchText,
        isEditModalOpen,
        isCreateModalOpen,
        isDeleteModalOpen,
        isLoading,
        isEditing,
        isCreating,
        isDeleting,
        handleEditAsignatura,
        toggleEditModal,
        toggleCreateModal,
        toggleDeleteModal,
        asignaturaEdit,
        asignaturaCreate,
        asignaturaDelete,
      }}
    >
      {children}
    </AsignaturasContext.Provider>
  );
};

const useAsignaturas = () => useContext(AsignaturasContext);

export { AsignaturasProvider, useAsignaturas };
